import { useEffect, useState } from "react";
import { FocusTrap, Modal } from "@mantine/core";
import type { CorrectAnswer } from "@labeling-tools/api/field-rep/generated/types.gen";
import { TrackFrame } from "@/types/trackLabeling";
import {
  toCorrectAnswerCardContent,
  toLabelerAnswerCardContent,
} from "./AnswerCard";
import { AnswerFeedback } from "./AnswerFeedback";
import { describeCorrectAnswer, LabelerAnswer } from "./correctAnswer";
import { DisputeForm } from "./DisputeForm";

type CorrectAnswerDialogProps = {
  labelerAnswer: LabelerAnswer;
  correctAnswer: CorrectAnswer;
  labeledFrames: TrackFrame[];
  clearestLabeledFramePosition: number;
  /** Locked while the dispute is sent or the next track loads. */
  isLocked: boolean;
  onNextTrack: () => void;
  onSendDispute: (note: string) => Promise<void>;
};

/**
 * Shown after the labeler submits an incorrect answer: the feedback (the track, their answer, then
 * the correct one), or the dispute form.
 *
 * It has no keyboard shortcuts, so labelers need to spend time reading it instead of clicking a
 * button and move on.
 */
export const CorrectAnswerDialog = ({
  labelerAnswer,
  correctAnswer,
  labeledFrames,
  clearestLabeledFramePosition,
  isLocked,
  onNextTrack,
  onSendDispute,
}: CorrectAnswerDialogProps) => {
  const message = describeCorrectAnswer(labelerAnswer, correctAnswer);
  const labelerCardContent = toLabelerAnswerCardContent(labelerAnswer);
  const correctCardContent = toCorrectAnswerCardContent(correctAnswer);
  const [isDisputing, setIsDisputing] = useState(false);
  const [disputeNote, setDisputeNote] = useState("");

  // Whether the labeler has shown the correct answer. It lives here, not in AnswerFeedback, which
  // unmounts while the dispute form is open, so the answer stays shown after Cancel.
  const [isCorrectAnswerShown, setIsCorrectAnswerShown] = useState(false);

  // Makes the rest of the app inert while the dialog is open (the dialog renders outside #root),
  // so focus and clicks can't reach the page or the header behind the overlay: Mantine's focus
  // trap lets Shift+Tab out from its hidden first focus, onto the page's buttons.
  useEffect(() => {
    const appRoot = document.getElementById("root");
    if (!appRoot) {
      return;
    }

    appRoot.inert = true;
    return () => {
      appRoot.inert = false;
    };
  }, []);

  return (
    <Modal
      opened
      // Never closes by itself: no close button, outside click or Escape.
      onClose={() => {}}
      withCloseButton={false}
      closeOnClickOutside={false}
      closeOnEscape={false}
      centered
      size={1100}
      radius={28}
      padding={24}
      title={
        isDisputing ? "Dispute the correct answer" : "Your answer was incorrect"
      }
      styles={{
        inner: { left: 0 },
        // The answer view takes Mantine's maximum height (up to 900px), so the track viewer can
        // fill it. The dispute view keeps its own height.
        content: isDisputing
          ? undefined
          : {
              height: "min(calc(100dvh - var(--modal-y-offset) * 2), 900px)",
              display: "flex",
              flexDirection: "column",
            },
        body: isDisputing
          ? undefined
          : { flex: "1 0 auto", display: "flex", flexDirection: "column" },
        title: {
          fontSize: 24,
          fontWeight: 700,
          letterSpacing: "-0.01em",
          color: "#1e293b",
        },
      }}
    >
      {/* Keeps focus off the buttons, so the key that submitted can't press them. */}
      <FocusTrap.InitialFocus />
      {isDisputing ? (
        <DisputeForm
          labelerCardContent={labelerCardContent}
          correctCardContent={correctCardContent}
          note={disputeNote}
          onNoteChange={setDisputeNote}
          isLocked={isLocked}
          onCancel={() => setIsDisputing(false)}
          onNextTrack={onNextTrack}
          onSendDispute={onSendDispute}
        />
      ) : (
        <AnswerFeedback
          message={message}
          labelerCardContent={labelerCardContent}
          correctCardContent={correctCardContent}
          staffNote={correctAnswer.note}
          labeledFrames={labeledFrames}
          clearestLabeledFramePosition={clearestLabeledFramePosition}
          isCorrectAnswerShown={isCorrectAnswerShown}
          isLocked={isLocked}
          onShowCorrectAnswer={() => setIsCorrectAnswerShown(true)}
          onDispute={() => setIsDisputing(true)}
          onNextTrack={onNextTrack}
        />
      )}
    </Modal>
  );
};
